// lightweight-charts with the terminal's look, plus indicator math.

import {
  ColorType,
  CrosshairMode,
  createChart,
  type ChartOptions,
  type DeepPartial,
  type IChartApi,
  type UTCTimestamp,
} from "../vendor/lwc.mjs";

export const COLORS = {
  up: "#3ddc5f",
  down: "#ff4b4b",
  amber: "#ffa028",
  series: ["#ffa028", "#6fb6ff", "#ff6fd8", "#3ddc5f", "#ffe14d", "#b48cff", "#ff8c5a", "#5ad7d7"],
};

export const THEME: DeepPartial<ChartOptions> = {
  autoSize: true,
  layout: {
    background: { type: ColorType.Solid, color: "#000" },
    textColor: "#9a9a9a",
    fontFamily: getComputedStyle(document.documentElement).getPropertyValue("--font") || "monospace",
    fontSize: 11,
    panes: { separatorColor: "#2e2e2e", separatorHoverColor: "#444" },
  },
  grid: { vertLines: { color: "#161616" }, horzLines: { color: "#161616" } },
  crosshair: {
    mode: CrosshairMode.Normal,
    vertLine: { color: "#666", labelBackgroundColor: "#ffa028" },
    horzLine: { color: "#666", labelBackgroundColor: "#ffa028" },
  },
  rightPriceScale: { borderColor: "#2e2e2e" },
  timeScale: { borderColor: "#2e2e2e", timeVisible: true, secondsVisible: false },
};

/// A chart filling `container`, removed when `signal` aborts.
export function makeChart(container: HTMLElement, signal: AbortSignal, opts: DeepPartial<ChartOptions> = {}): IChartApi {
  const chart = createChart(container, { ...THEME, ...opts });
  signal.addEventListener("abort", () => chart.remove());
  return chart;
}

/// Unix seconds shifted so the chart's UTC axis shows exchange-local time.
export function localTime(t: number, gmtoffset = 0): UTCTimestamp {
  return (t + gmtoffset) as UTCTimestamp;
}

type Num = number | null;

export function sma(v: Num[], n: number): Num[] {
  const out: Num[] = [];
  let sum = 0;
  let count = 0;
  for (let i = 0; i < v.length; i++) {
    const x = v[i];
    if (x !== null) {
      sum += x;
      count++;
    }
    const drop = i >= n ? v[i - n] : null;
    if (i >= n && drop !== null) {
      sum -= drop;
      count--;
    }
    out.push(i >= n - 1 && count === n ? sum / n : null);
  }
  return out;
}

export function ema(v: Num[], n: number): Num[] {
  const k = 2 / (n + 1);
  const out: Num[] = [];
  let prev: number | null = null;
  let seed: number[] = [];
  for (const x of v) {
    if (x === null) {
      out.push(prev);
      continue;
    }
    if (prev === null) {
      seed.push(x);
      if (seed.length === n) {
        prev = seed.reduce((a, b) => a + b, 0) / n;
        seed = [];
      }
      out.push(prev);
    } else {
      prev = x * k + prev * (1 - k);
      out.push(prev);
    }
  }
  return out;
}

export function bollinger(v: Num[], n = 20, mult = 2): { mid: Num[]; hi: Num[]; lo: Num[] } {
  const mid = sma(v, n);
  const hi: Num[] = [];
  const lo: Num[] = [];
  for (let i = 0; i < v.length; i++) {
    const m = mid[i];
    if (m === null) {
      hi.push(null);
      lo.push(null);
      continue;
    }
    let ss = 0;
    for (let j = i - n + 1; j <= i; j++) ss += ((v[j] ?? m) - m) ** 2;
    const sd = Math.sqrt(ss / n);
    hi.push(m + mult * sd);
    lo.push(m - mult * sd);
  }
  return { mid, hi, lo };
}

/// Wilder's RSI.
export function rsi(v: number[], n = 14): Num[] {
  const out: Num[] = [null];
  let gain = 0;
  let loss = 0;
  for (let i = 1; i < v.length; i++) {
    const d = v[i] - v[i - 1];
    const g = Math.max(d, 0);
    const l = Math.max(-d, 0);
    if (i <= n) {
      gain += g / n;
      loss += l / n;
      out.push(i === n ? 100 - 100 / (1 + gain / (loss || 1e-12)) : null);
    } else {
      gain = (gain * (n - 1) + g) / n;
      loss = (loss * (n - 1) + l) / n;
      out.push(100 - 100 / (1 + gain / (loss || 1e-12)));
    }
  }
  return out;
}

export function macd(v: number[], fast = 12, slow = 26, signal = 9): { macd: Num[]; signal: Num[]; hist: Num[] } {
  const ef = ema(v, fast);
  const es = ema(v, slow);
  const line = ef.map((x, i) => (x !== null && es[i] !== null ? x - es[i]! : null));
  const sig = ema(line, signal);
  const hist = line.map((x, i) => (x !== null && sig[i] !== null ? x - sig[i]! : null));
  return { macd: line, signal: sig, hist };
}
